import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import cloneDeep from 'lodash/cloneDeep';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

import UserProfile from "../../Component/Normal/UserProfile";

import FetchModule from "../../Model/Share/Network/FetchModule";
import { makeUpWorkout } from "../Transaction/Transaction";
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import DefaultLogoPath from '../../image/DefaultLogo.png';
import WhiteExercisePlusPath from '../../image/WhiteExercisePlus.png';
import OrangeExercisePlusPath from '../../image/OrangeExercisePlus.png';
import WhitePenPath from '../../image/WhitePen.png';
import RedLockerPath from '../../image/RedLocker.png';
import GrayLockerPath from '../../image/GrayLocker.png';
import { ReactComponent as LockIcon } from '../../image/Lock.svg';
import { ReactComponent as UnlockIcon } from '../../image/Unlock.svg';
import { ReactComponent as HoverLockIcon } from '../../image/HoverLock.svg';
import TrashIconPath from '../../image/Trash.svg';
import TrashHoverIconPath from '../../image/TrashHover.svg';
import MoveIconPath from '../../image/Move.svg';
import ClickMoveIconPath from '../../image/ClickMove.svg';

export default React.memo(function WodBox(props) {
  const {
    storeLogoSize,
    sizeDivision,
    storeTextBoxWidth,
    today,
    data,
    weekIndex,
    dayIndex,
    clickWeekIndex,
    clickDayIndex,
    clickWodIndex,
    onClickWod,

    dates,
    refreshTotalData,

    copyWodInfo,
    setCopyWodInfo,
    lastCopyWodInfo,
    setLastCopyWodInfo,
    isViewCompleteCopy,

    viewStoreIds,

    setOpenPopUp,
    setLockPopUp,
    onClickDeleteWod,
    activeDragId
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const dragId = weekIndex + '-' + dayIndex;

  const { setNodeRef: setDropNodeRef, isOver } = useDroppable({
    id: dragId
  });

  // 캐싱된 날짜 문자열 //
  const todayFormatted = useMemo(() => today.format('YYYY.M.D'), [today]);
  const dateFormatted = useMemo(() => data.date.format('YYYY.M.D'), [data.date]);
  const isToday = todayFormatted === dateFormatted;

  // 현재 클릭되어있는지 여부 결정 //
  const isClickedWod = weekIndex === clickWeekIndex && dayIndex === clickDayIndex;

  // 와드가 저장되어있는지 여부 결정 //
  const hasWods = data.wods.length !== 0;

  // 날짜 표출여부 결정 //
  const showDate = isToday ? sizeDivision === 'Max' : true;

  // EmptyWodBox 표출 여부 결정 //
  const showEmptyWodBox = sizeDivision === 'Max' && !hasWods;

  // 와드 추가버튼 테두리/배경색 //
  const wodAddButtonBorder = useMemo(() => {
    if (isClickedWod && sizeDivision === 'Min') return `1px solid ${theme.primaryHover}`;
    else if (hasWods) return `1px dashed ${theme.primary}`;
    else return `1px solid ${theme.primary}`;
  }, [isClickedWod, sizeDivision, hasWods, theme.primaryHover, theme.primary]);

  const wodAddButtonBgColor = useMemo(() => {
    if (isClickedWod && sizeDivision === 'Min') return theme.primaryHover;
    else if (hasWods) return theme.subBackground;
    else return theme.primary;
  }, [isClickedWod, sizeDivision, hasWods, theme.primaryHover, theme.subBackground, theme.primary]);

  // 붙여넣기 실행 //
  const onClickPaste = useCallback(function() {
    if (copyWodInfo === undefined) return;
    else if (copyWodInfo.classDate === data.wods[clickWodIndex].classDate
      && copyWodInfo.weekIndex === clickWeekIndex
      && copyWodInfo.dayIndex === clickDayIndex
      && copyWodInfo.wodIndex === clickWodIndex) {
      setCopyWodInfo(undefined);
    }
    else {
      let getPastedWod = dates[clickWeekIndex][clickDayIndex].wods[clickWodIndex];
      let copyWod = cloneDeep(copyWodInfo.copyWod);

      copyWod.workoutId = getPastedWod.workoutId;

      // Section ID 없애기 //
      for (let i = 0; i < copyWod.sectionList.length; i++) {
        copyWod.sectionList[i].sectionId = undefined;

        if (copyWod.sectionList[i].scaleSections !== undefined) {
          // ScaleSection ID Setting //
          for (let j = 0; j > copyWod.sectionList[i].scaleSections.length; j++) {
            copyWod.sectionList[i].scaleSections[j].sectionId = undefined;
            copyWod.sectionList[i].scaleSections[j].derivationSectionId = undefined;

            for (let h = 0; h < copyWod.sectionList[i].scaleSections[j].mainExerciseList.length; h++) {
              copyWod.sectionList[i].scaleSections[j].mainExerciseList[h].sectionId = undefined;
              copyWod.sectionList[i].scaleSections[j].mainExerciseList[h].exerciseId = undefined;
            }
          }
        }

        if (copyWod.sectionList[i].buyinExerciseList !== undefined) copyWod.sectionList[i].buyinExerciseList.map((data) => (data.exerciseId = undefined, data.sectionId = undefined));
        if (copyWod.sectionList[i].buyinScaleOptionExerciseList !== undefined) copyWod.sectionList[i].buyinScaleOptionExerciseList.map((data) => (data.exerciseId = undefined, data.sectionId = undefined));
        if (copyWod.sectionList[i].mainExerciseList !== undefined) copyWod.sectionList[i].mainExerciseList.map((data) => (data.exerciseId = undefined, data.sectionId = undefined));
        if (copyWod.sectionList[i].mainScaleOptionExerciseList !== undefined) copyWod.sectionList[i].mainScaleOptionExerciseList.map((data) => (data.exerciseId = undefined, data.sectionId = undefined));
        if (copyWod.sectionList[i].emomExerciseList !== undefined) copyWod.sectionList[i].emomExerciseList.map((data) => (data.exerciseId = undefined, data.sectionId = undefined));
        if (copyWod.sectionList[i].emomScaleOptionExerciseList !== undefined) copyWod.sectionList[i].emomScaleOptionExerciseList.map((data) => (data.exerciseId = undefined, data.sectionId = undefined));
        if (copyWod.sectionList[i].buyoutExerciseList !== undefined) copyWod.sectionList[i].buyoutExerciseList.map((data) => (data.exerciseId = undefined, data.sectionId = undefined));
        if (copyWod.sectionList[i].buyoutScaleOptionExerciseList !== undefined) copyWod.sectionList[i].buyoutScaleOptionExerciseList.map((data) => (data.exerciseId = undefined, data.sectionId = undefined));
      }

      dates[clickWeekIndex][clickDayIndex].wods[clickWodIndex] = copyWod;
      dates[clickWeekIndex][clickDayIndex].wods[clickWodIndex].isCompleteSaved = false;

      refreshTotalData();

      setLastCopyWodInfo({
        weekIndex: clickWeekIndex,
        dayIndex: clickDayIndex,
        wodIndex: clickWodIndex
      });
    }
  }, [copyWodInfo, data, clickWeekIndex, clickDayIndex, clickWodIndex, dates, setCopyWodInfo, refreshTotalData, setLastCopyWodInfo]);

  // 잠금버튼 클릭시 //
  const onClickLockButton = useCallback(function(weekIndex, dayIndex, wodIndex) {
    if (dates[weekIndex][dayIndex].wods[wodIndex].workoutId === undefined) {
      dates[weekIndex][dayIndex].wods[wodIndex].isLock = !dates[weekIndex][dayIndex].wods[wodIndex].isLock;
      dates[weekIndex][dayIndex].wods[wodIndex].isCompleteSaved = false;
      refreshTotalData();

      if (dates[weekIndex][dayIndex].wods[wodIndex].isLock) {
        setLockPopUp({
          width: 189,
          marginTop: -60,
          opacity: 1
        })

        setTimeout(() => (
          setLockPopUp({
            width: 0,
            marginTop: 35,
            opacity: 0
          })
        ), 1500)
      }
      else {
        setOpenPopUp({
          width: 214,
          marginTop: -60,
          opacity: 1
        })

        setTimeout(() => (
          setOpenPopUp({
            width: 0,
            marginTop: 35,
            opacity: 0
          })
        ), 1500)
      }
    }
    else {
      let requestData = {
        workoutId: dates[weekIndex][dayIndex].wods[wodIndex].workoutId
      }

      const fetchModule = new FetchModule();
      (
        async () => {
          const responseData = await fetchModule.postDataOfExerciseVersion2('workout/replace_lock', 'POST', requestData);
          if (responseData.status === 200) {
            dates[weekIndex][dayIndex].wods[wodIndex].isLock = !dates[weekIndex][dayIndex].wods[wodIndex].isLock;
            refreshTotalData();

            if (dates[weekIndex][dayIndex].wods[wodIndex].isLock) {
              setLockPopUp({
                width: 189,
                marginTop: -60,
                opacity: 1
              })

              setTimeout(() => (
                setLockPopUp({
                  width: 0,
                  marginTop: 35,
                  opacity: 0
                })
              ), 1500)
            }
            else {
              setOpenPopUp({
                width: 214,
                marginTop: -60,
                opacity: 1
              })

              setTimeout(() => (
                setOpenPopUp({
                  width: 0,
                  marginTop: 35,
                  opacity: 0
                })
              ), 1500)
            }
          }
          else if (responseData.status === 201) {
            let saveStatus = dates[weekIndex][dayIndex].wods[wodIndex].isCompleteSaved;

            let newWod = [];
            newWod.push(cloneDeep(responseData.data));

            let finalWod = makeUpWorkout(newWod)[0];

            dates[weekIndex][dayIndex].wods[wodIndex] = finalWod;
            dates[weekIndex][dayIndex].wods[wodIndex].isCompleteSaved = saveStatus;
            refreshTotalData();

            if (dates[weekIndex][dayIndex].wods[wodIndex].isLock) {
              setLockPopUp({
                width: 189,
                marginTop: -60,
                opacity: 1
              })

              setTimeout(() => (
                setLockPopUp({
                  width: 0,
                  marginTop: 35,
                  opacity: 0
                })
              ), 1500)
            }
            else {
              setOpenPopUp({
                width: 214,
                marginTop: -60,
                opacity: 1
              })

              setTimeout(() => (
                setOpenPopUp({
                  width: 0,
                  marginTop: 35,
                  opacity: 0
                })
              ), 1500)
            }
          }
        }
      )();
    }
  }, [dates, refreshTotalData, setLockPopUp, setOpenPopUp]);

  // 보여줄지 말지 여부 결정 //
  const decideViewWod = useCallback(function(wod) {
    if (viewStoreIds === undefined) return true;
    else if (wod.storeId === undefined) return true;
    else if (viewStoreIds.includes(wod.storeId)) return true;
    else return false;
  }, [viewStoreIds]);

  // 복사 붙여넣기 진행시 붙여넣을 와드 표시 //
  const onViewWillCopyWod = useCallback(function() {
    if (copyWodInfo === undefined) return;

    const copyIsLocked = sizeDivision !== 'Min' && copyWodInfo.copyWod.isLock;
    const copyLogoSize = sizeDivision === 'Max' ? 31 : storeLogoSize;
    const copyLogoLocked = sizeDivision === 'Min' && copyWodInfo.copyWod.isLock;
    const copyShowLogoLock = sizeDivision !== 'Max' && copyWodInfo.copyWod.isLock;
    const copyShowText = sizeDivision !== 'Min';
    const copyTextWidth = sizeDivision === 'Max' ? storeTextBoxWidth + 'px' : 'auto';
    const copyTextHeight = sizeDivision === 'Max' ? '15px' : '30px';

    function copySectionTitle(d) {
      if (d.tag === 'ForTime') return d.round + ' Round For Time';
      else if (d.tag === 'AMRAP') {
        let min = parseInt(d.exerciseTime / 60), sec = parseInt(d.exerciseTime % 60);
        if (min > 0 && sec > 0) return min + 'min ' + sec + 'sec AMRAP';
        else if (min > 0) return min + 'min AMRAP';
        else if (sec > 0) return sec + 'sec AMRAP';
        else return 'AMRAP';
      }
      else if (d.tag === 'EMOM') {
        let min = parseInt(d.round * d.exerciseTime / 60), sec = parseInt(d.round * d.exerciseTime % 60);
        if (min > 0 && sec > 0) return min + 'min ' + sec + 'sec EMOM';
        else if (min > 0) return min + ' min EMOM';
        else if (sec > 0) return sec + ' sec EMOM';
        else return 'EMOM';
      }
      else if (d.tag === 'TABATA') {
        const pad = (n) => (n + '').padStart(2, '0');
        return 'On: ' + parseInt(d.exerciseTime / 60) + ':' + pad(parseInt(d.exerciseTime % 60)) + ' / Off:' + parseInt(d.restTime / 60) + ':' + pad(parseInt(d.restTime % 60)) + ' ' + d.round + 'R TABATA';
      }
      else if (d.tag === 'SINGLE') {
        if (d.mainExerciseList.length <= 0) return 'SINGLE';
        else if (d.mainExerciseList.length === 1) return d.mainExerciseList[0].movementName;
        else return 'Multiple Single';
      }
      else if (d.tag === 'Custom') {
        if (d.customTitle !== '') return d.customTitle;
        else if (d.scoreKeyword === '점수 없음' || d.scoreKeyword === '피크와트' || d.scoreKeyword === '최고 속도') return 'Custom : ' + d.scoreKeyword;
        else return 'Custom : ' + d.scoreKeyword + ' ' + d.scorePriority;
      }
    }

    return (
      <WodBoxWrapper
        style={{ opacity: 0.4, zIndex: 3 }}
        border={`2px solid ${theme.subBackground}`}
        bgColor={theme.subBackground}
        isLock={copyIsLocked}
        tabIndex={0}>
        <WodStoreWrapper>
          <StoreInfoWrapper isMid={sizeDivision === 'Mid'}>
            <StoreLogoWrapper size={copyLogoSize} isLock={copyLogoLocked}>
              {
                copyShowLogoLock &&
                <LockerWrapper style={{ position: 'absolute' }}>
                  <LockerImage src={RedLockerPath} />
                </LockerWrapper>
              }
              <UserProfile profile={copyWodInfo.copyWod.storeLogo} defaultProfile={DefaultLogoPath} />
            </StoreLogoWrapper>
            {
              copyShowText &&
              <StoreTextInfoWrapper>
                <TextWrapper width={copyTextWidth} height='15px'>
                  <Text fontFamily='NotoSansKR-Bold' fontSize={10} color={theme.text} hover='pointer'>{copyWodInfo.copyWod.lessonName}</Text>
                </TextWrapper>
                <TextWrapper width={copyTextWidth} height={copyTextHeight}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={10} color={theme.text} hover='pointer'>{copyWodInfo.copyWod.storeName}</Text>
                </TextWrapper>
              </StoreTextInfoWrapper>
            }
          </StoreInfoWrapper>
        </WodStoreWrapper>
        {
          (copyWodInfo.copyWod.wodVersion === 2 && sizeDivision !== 'Min') &&
          <WriteWodBox bgColor={theme.subBackground}>
            {
              copyWodInfo.copyWod.sectionList.map((sectionData, sectionIndex) => (
                <WodPrevViewWrapper key={'Prev-' + sectionIndex}>
                  <WodPrevViewSectionTitleWrapper>
                    <SectionBox>
                      <SmallText style={{ marginTop: '2px' }} fontFamily='Poppins-SemiBold' fontSize={10} color={theme.text} hover='pointer'>{String.fromCharCode(sectionIndex + 65)}</SmallText>
                    </SectionBox>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={10} color={theme.text} hover='pointer'>{copySectionTitle(sectionData)}</Text>
                  </WodPrevViewSectionTitleWrapper>
                  <PreText fontFamily='NotoSansKR-Regular' fontSize={10} color={theme.text} hover='pointer'>{sectionData.mainProgram}</PreText>
                </WodPrevViewWrapper>
              ))
            }
          </WriteWodBox>
        }
      </WodBoxWrapper>
    )
  }, [copyWodInfo, sizeDivision, storeLogoSize, storeTextBoxWidth]);

  // 캐싱된 짧은 날짜 문자열 //
  const dateShort = useMemo(() => data.date.format('M.D'), [data.date]);
  const dateFull = useMemo(() => data.date.format('YYYY.M.D'), [data.date]);

  return (
    <WodContainer ref={setDropNodeRef} isOver={isOver && activeDragId !== null && activeDragId !== dragId} bgColor={theme.subBackground}>
      <TotalWrapper>
        {
          sizeDivision !== 'Max'
          ?
          <DateBox isToday={isToday} isMinimum={sizeDivision !== 'Max'} bgColor={theme.subBackground}>
            {
              showDate &&
              <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={isToday ? theme.white : theme.text}>{sizeDivision === 'Min' ? dateShort : dateFull}</Text>
            }
            {
              isToday &&
              <Text fontFamily='NotoSansKR-Bold' fontSize={10} color={theme.subBackground}>TODAY</Text>
            }
          </DateBox>
          :
          isToday &&
          <DateBox isToday={true} isMinimum={true} bgColor={theme.subBackground}>
            <Text fontFamily='NotoSansKR-Bold' fontSize={10} color={theme.subBackground}>TODAY</Text>
          </DateBox>
        }
        {
          showEmptyWodBox &&
          <EmptyWodBox onClick={() => onClickWod(data.date, undefined, weekIndex, dayIndex, undefined)}>

          </EmptyWodBox>
        }
        {
          data.wods.map((wodData, wodIndex) => (
            decideViewWod(wodData) &&
            <DraggableWodCard
              key={'Wod-' + weekIndex + '-' + dayIndex + '-' + wodIndex}
              wodData={wodData}
              wodIndex={wodIndex}
              weekIndex={weekIndex}
              dayIndex={dayIndex}
              data={data}
              today={today}
              sizeDivision={sizeDivision}
              storeLogoSize={storeLogoSize}
              storeTextBoxWidth={storeTextBoxWidth}
              dateFull={dateFull}
              clickWeekIndex={clickWeekIndex}
              clickDayIndex={clickDayIndex}
              clickWodIndex={clickWodIndex}
              copyWodInfo={copyWodInfo}
              lastCopyWodInfo={lastCopyWodInfo}
              dates={dates}
              onClickWod={onClickWod}
              onClickDeleteWod={onClickDeleteWod}
              onClickLockButton={onClickLockButton}
              isViewCompleteCopy={isViewCompleteCopy}
              onClickPaste={onClickPaste}
              setCopyWodInfo={setCopyWodInfo}
              onViewWillCopyWod={onViewWillCopyWod} />
          ))
        }
        {
          <WodAddButton
            isMinimum={sizeDivision === 'Min'}
            isClicked={isClickedWod}
            border={wodAddButtonBorder}
            backgroundColor={wodAddButtonBgColor}
            onClick={() => onClickWod(data.date, undefined, weekIndex, dayIndex, undefined)}>
            <PenImage src={WhitePenPath} isMinimum={sizeDivision === 'Min'} isClicked={(isClickedWod && sizeDivision === 'Min')} />
            <PlusImage src={hasWods ? OrangeExercisePlusPath : WhiteExercisePlusPath} isMinimum={sizeDivision === 'Min'} isClicked={(isClickedWod && sizeDivision === 'Min')} />
          </WodAddButton>
        }
      </TotalWrapper>
    </WodContainer>
  )
})

const DraggableWodCard = React.memo(function DraggableWodCard({
  wodData, wodIndex, weekIndex, dayIndex, data, today, sizeDivision,
  storeLogoSize, storeTextBoxWidth, dateFull,
  clickWeekIndex, clickDayIndex, clickWodIndex,
  copyWodInfo, lastCopyWodInfo, dates,
  onClickWod, onClickDeleteWod, onClickLockButton,
  isViewCompleteCopy,
  onClickPaste, setCopyWodInfo, onViewWillCopyWod
}) {
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  // 내부 계산 함수들 (원시값 기반) //
  const isLocked = sizeDivision !== 'Min' && wodData.isLock;
  const logoSize = sizeDivision === 'Max' ? 31 : storeLogoSize;
  const showStoreText = sizeDivision !== 'Min';
  const textWrapperWidth = sizeDivision === 'Max' ? storeTextBoxWidth + 'px' : 'auto';
  const textWrapperHeight = sizeDivision === 'Max' ? '15px' : '30px';
  const logoLocked = sizeDivision === 'Min' && wodData.isLock;
  const showLogoLock = sizeDivision !== 'Max' && wodData.isLock;

  const border = useMemo(() => {
    if (copyWodInfo === undefined) {
      if (weekIndex === clickWeekIndex && dayIndex === clickDayIndex && wodIndex === clickWodIndex) return `2px solid ${theme.primary}`;
      else return `2px solid ${theme.subBackground}`;
    }
    else {
      if (dates[weekIndex][dayIndex].wods[wodIndex].classDate == copyWodInfo.classDate
        && weekIndex === copyWodInfo.weekIndex
        && dayIndex === copyWodInfo.dayIndex
        && wodIndex === copyWodInfo.wodIndex) return `2px solid ${theme.subKey}`;
      else if (weekIndex === clickWeekIndex && dayIndex === clickDayIndex && wodIndex === clickWodIndex) return `2px solid ${theme.primary}`;
      else return `2px solid ${theme.subBackground}`;
    }
  }, [copyWodInfo, clickWeekIndex, clickDayIndex, clickWodIndex, weekIndex, dayIndex, wodIndex, dates, theme.primary, theme.subBackground, theme.subKey]);

  const copyHeaderOpacity = useMemo(() => {
    if (copyWodInfo === undefined) return 0;
    if (isViewCompleteCopy
      && copyWodInfo.classDate === wodData.classDate
      && copyWodInfo.weekIndex === weekIndex
      && copyWodInfo.dayIndex === dayIndex
      && copyWodInfo.wodIndex === wodIndex) return 1;
    return 0;
  }, [copyWodInfo, isViewCompleteCopy, wodData.classDate, weekIndex, dayIndex, wodIndex]);

  const classDateFormatted = useMemo(() => data.date.format('YYYY-MM-DDT00:00:00'), [data.date]);

  const pasteVisible = useMemo(() => {
    if (copyWodInfo === undefined) return 0;
    const classDate = classDateFormatted;
    if (copyWodInfo.classDate !== classDate
      && clickWeekIndex === weekIndex
      && clickDayIndex === dayIndex
      && clickWodIndex === wodIndex) {
      if (lastCopyWodInfo === undefined
        || lastCopyWodInfo.weekIndex !== weekIndex
        || lastCopyWodInfo.dayIndex !== dayIndex
        || lastCopyWodInfo.wodIndex !== wodIndex) return 1;
      return 0;
    }
    return 0;
  }, [copyWodInfo, lastCopyWodInfo, clickWeekIndex, clickDayIndex, clickWodIndex, weekIndex, dayIndex, wodIndex, classDateFormatted]);

  const decideSectionTitle = useCallback(function(sectionData) {
    if (sectionData.tag === 'ForTime') return sectionData.round + ' Round For Time';
    else if (sectionData.tag === 'AMRAP') {
      let min = parseInt(sectionData.exerciseTime / 60);
      let sec = parseInt(sectionData.exerciseTime % 60);
      if (min > 0 && sec > 0) return min + 'min ' + sec + 'sec AMRAP';
      else if (min > 0) return min + 'min AMRAP';
      else if (sec > 0) return sec + 'sec AMRAP';
      else return 'AMRAP';
    }
    else if (sectionData.tag === 'EMOM') {
      let min = parseInt(sectionData.round * sectionData.exerciseTime / 60);
      let sec = parseInt(sectionData.round * sectionData.exerciseTime % 60);
      if (min > 0 && sec > 0) return min + 'min ' + sec + 'sec EMOM';
      else if (min > 0) return min + ' min EMOM';
      else if (sec > 0) return sec + ' sec EMOM';
      else return 'EMOM';
    }
    else if (sectionData.tag === 'TABATA') {
      let onMin = parseInt(sectionData.exerciseTime / 60);
      let onSec = parseInt(sectionData.exerciseTime % 60);
      let offMin = parseInt(sectionData.restTime / 60);
      let offSec = parseInt(sectionData.restTime % 60);
      const pad = (n) => (n + '').padStart(2, '0');
      return 'On: ' + onMin + ':' + pad(onSec) + ' / Off:' + offMin + ':' + pad(offSec) + ' ' + sectionData.round + 'R TABATA';
    }
    else if (sectionData.tag === 'SINGLE') {
      if (sectionData.mainExerciseList.length <= 0) return 'SINGLE';
      else if (sectionData.mainExerciseList.length === 1) return sectionData.mainExerciseList[0].movementName;
      else return 'Multiple Single';
    }
    else if (sectionData.tag === 'Custom') {
      if (sectionData.customTitle !== '') return sectionData.customTitle;
      else if (sectionData.scoreKeyword === '점수 없음'
        || sectionData.scoreKeyword === '피크와트'
        || sectionData.scoreKeyword === '최고 속도') return 'Custom : ' + sectionData.scoreKeyword;
      else return 'Custom : ' + sectionData.scoreKeyword + ' ' + sectionData.scorePriority;
    }
  }, []);

  const wodDragId = 'drag-' + weekIndex + '-' + dayIndex + '-' + wodIndex;
  const { attributes: wodDragAttributes, listeners: wodDragListeners, setNodeRef: setWodDragRef, transform: wodTransform, isDragging: wodIsDragging } = useDraggable({
    id: wodDragId
  });

  const wodDragStyle = useMemo(() => ({
    transform: CSS.Translate.toString(wodTransform),
    zIndex: wodIsDragging ? 1000 : undefined,
    opacity: wodIsDragging ? 1 : undefined,
    position: wodIsDragging ? 'relative' : undefined,
    willChange: wodIsDragging ? 'transform' : undefined,
  }), [wodTransform, wodIsDragging]);

  // 삭제 버튼 클릭 //
  function onClickTrashButton(e) {
    e.stopPropagation();
    if (onClickDeleteWod) onClickDeleteWod(weekIndex, dayIndex, wodIndex);
  }

  // 잠금 버튼 클릭 //
  function onClickLockButtonInner(e) {
    e.stopPropagation();
    onClickLockButton(weekIndex, dayIndex, wodIndex);
  }

  return (
    <WodBoxWrapper
      ref={setWodDragRef}
      id={'Wod-' + weekIndex.toString() + '-' + dayIndex.toString() + '-' + wodIndex.toString()}
      border={border}
      bgColor={theme.subBackground}
      isLock={isLocked}
      onClick={() => onClickWod(data.date, wodData, weekIndex, dayIndex, wodIndex)}
      style={wodDragStyle}>
      {
        sizeDivision === 'Max' &&
        <WodActionBar>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} hover='pointer'>{dateFull}</Text>
          <DateActionWrapper>
            <TrashButton onClick={onClickTrashButton} title='삭제'>
              <TrashIconDefault src={TrashIconPath} />
              <TrashIconHover src={TrashHoverIconPath} />
            </TrashButton>
            <LockButton onClick={onClickLockButtonInner} title='잠금'>
              <LockIconDefault locked={wodData.isLock}><LockIcon width={11} height={14} /></LockIconDefault>
              <UnlockIconDefault locked={wodData.isLock}><UnlockIcon width={11} height={14} /></UnlockIconDefault>
              <HoverLockIconWrapper locked={wodData.isLock}><HoverLockIcon width={11} height={14} /></HoverLockIconWrapper>
            </LockButton>
            <MoveButton {...wodDragListeners} {...wodDragAttributes} title='드래그' onClick={(e) => e.stopPropagation()}>
              <MoveIconDefault src={MoveIconPath} />
              <MoveIconHover src={ClickMoveIconPath} />
            </MoveButton>
          </DateActionWrapper>
        </WodActionBar>
      }
      <WodContentWrapper isLock={isLocked}>
        <CopyWodHeader
          isViewCompleteCopy={isViewCompleteCopy}
          opacity={copyHeaderOpacity}>
          <Text fontFamily='NotoSansKR-Bold' fontSize={12} color={theme.white} hover='pointer'>복사 완료</Text>
        </CopyWodHeader>
        {
          sizeDivision !== 'Min' &&
          <PasteWodTotalWrapper isView={pasteVisible}>
          <PasteWodHeader opacity={pasteVisible} bgColor={theme.subBackground}>
            <PasteButtonWrapper>
              <PasteButton onClick={onClickPaste}>
                <Text fontFamily='NotoSansKR-Bold' fontSize={12} color={theme.white} hover='pointer'>붙여 넣기</Text>
              </PasteButton>
              <PasteExitButton onClick={() => setCopyWodInfo(undefined)} bgColor={theme.subBackground}>
                <ExitButton>
                  <ExitWidth width={14} height={2} radius={4} />
                  <ExitWidth width={2} height={14} radius={4} />
                </ExitButton>
              </PasteExitButton>
            </PasteButtonWrapper>
            {onViewWillCopyWod()}
          </PasteWodHeader>
        </PasteWodTotalWrapper>
        }
        <WodStoreWrapper>
          <StoreInfoWrapper isMid={sizeDivision === 'Mid'}>
            <StoreLogoWrapper size={logoSize} isLock={logoLocked}>
              {
                showLogoLock &&
                <LockerWrapper style={{ position: 'absolute' }}>
                  <LockerImage src={wodData.isLock ? RedLockerPath : GrayLockerPath} />
                </LockerWrapper>
              }
              <UserProfile profile={wodData.storeLogo} defaultProfile={DefaultLogoPath} />
            </StoreLogoWrapper>
            {
              showStoreText &&
              <StoreTextInfoWrapper>
                <TextWrapper width={textWrapperWidth} height='15px'>
                  <Text fontFamily='NotoSansKR-Bold' fontSize={10} color={theme.text} hover='pointer'>{wodData.lessonName}</Text>
                </TextWrapper>
                <TextWrapper width={textWrapperWidth} height={textWrapperHeight}>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={10} color={theme.text} hover='pointer'>{wodData.storeName}</Text>
                </TextWrapper>
              </StoreTextInfoWrapper>
            }
          </StoreInfoWrapper>
        </WodStoreWrapper>
        {
          (wodData.wodVersion === 2 && sizeDivision !== 'Min') &&
          <WriteWodBox bgColor={theme.subBackground}>
            {
              wodData.sectionList.map((sectionData, sectionIndex) => (
                <WodPrevViewWrapper key={'Prev-' + sectionIndex}>
                  <WodPrevViewSectionTitleWrapper>
                    <SectionBox>
                      <SmallText style={{ marginTop: '2px' }} fontFamily='Poppins-SemiBold' fontSize={10} color={theme.white} hover='pointer'>{String.fromCharCode(sectionIndex + 65)}</SmallText>
                    </SectionBox>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={10} color={theme.text} hover='pointer'>{decideSectionTitle(sectionData)}</Text>
                  </WodPrevViewSectionTitleWrapper>
                  {
                    sectionData.tag !== 'SINGLE' &&
                    <PreText fontFamily='NotoSansKR-Regular' fontSize={10} color={theme.text} hover='pointer'>{sectionData.mainProgram}</PreText>
                  }
                </WodPrevViewWrapper>
              ))
            }
          </WriteWodBox>
        }
        {
          !wodData.isCompleteSaved &&
          <SavingBox>
            <Text fontFamily='NotoSansKR-Bold' fontSize={10} color={theme.text} hover='default'>작성중</Text>
          </SavingBox>
        }
      </WodContentWrapper>
    </WodBoxWrapper>
  );
});

const WodContainer = styled.section`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  padding: 5px 0;

  width: 100%;

  border-radius: 5px;
  background-color: ${props => props.isOver ? `rgba(255, 139, 72, 0.15)` : props.bgColor};

  transition: background-color 0.2s ease-in-out;
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 96%;

  gap: 10px;
`;

const DateBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: ${props => props.isMinimum ? `center` : `space-between`};
  align-items: center;

  padding: 0px 4px 0px 4px;

  width: calc(100% - 8px);
  height: 22px;

  border-radius: ${props => props.isToday ? `3px 3px 0px 0px` : `3px`};
  background-color: ${props => props.isToday ? `#6DD49E` : props.bgColor};
`;

const WodActionBar = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  justify-content: space-between;
  align-items: center;

  padding: 4px 4px 0px 4px;
  margin-bottom: 20px;

  width: calc(100% - 8px);

  row-gap: 2px;
`;

const DateActionWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  gap: 4px;
`;

const TrashButton = styled.div`
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 16px;
  height: 16px;

  cursor: pointer;
`;

const TrashIconDefault = styled.img`
  position: absolute;
  width: 15px;
  height: 16px;

  opacity: 1;
  transition: opacity 0.3s ease-in-out;

  ${TrashButton}:hover & {
    opacity: 0;
  }
`;

const TrashIconHover = styled.img`
  position: absolute;
  width: 15px;
  height: 16px;

  opacity: 0;
  transition: opacity 0.3s ease-in-out;

  ${TrashButton}:hover & {
    opacity: 1;
  }
`;

const LockButton = styled.div`
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 16px;
  height: 16px;

  cursor: pointer;
`;

const LockIconDefault = styled.div`
  position: absolute;

  display: flex;
  justify-content: center;
  align-items: center;

  opacity: ${props => props.locked ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
`;

const UnlockIconDefault = styled.div`
  position: absolute;

  display: flex;
  justify-content: center;
  align-items: center;

  opacity: ${props => props.locked ? 0 : 1};
  transition: opacity 0.3s ease-in-out;

  ${LockButton}:hover & {
    opacity: 0;
  }
`;

const HoverLockIconWrapper = styled.div`
  position: absolute;

  display: flex;
  justify-content: center;
  align-items: center;

  opacity: 0;
  transition: opacity 0.3s ease-in-out;

  ${LockButton}:hover & {
    opacity: ${props => props.locked ? 0 : 1};
  }
`;

const MoveButton = styled.div`
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 16px;
  height: 16px;

  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

const MoveIconDefault = styled.img`
  position: absolute;
  width: 16px;
  height: 16px;

  opacity: 1;
  transition: opacity 0.3s ease-in-out;

  ${MoveButton}:hover &,
  ${MoveButton}:active & {
    opacity: 0;
  }
`;

const MoveIconHover = styled.img`
  position: absolute;
  width: 16px;
  height: 16px;

  opacity: 0;
  transition: opacity 0.3s ease-in-out;

  ${MoveButton}:hover &,
  ${MoveButton}:active & {
    opacity: 1;
  }
`;

const WodBoxWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: calc(100% - 4px);

  background-color: ${props => props.bgColor};

  border: ${props => props.border};
  border-radius: 4px;

  &:hover {
    cursor: pointer;
  }

  outline: none;

  transition: border 0.3s ease-in-out, opacity 0.3s ease-in-out;
`;

const WodContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  opacity: ${props => props.isLock ? '0.5' : '1'};

  transition: opacity 0.3s ease-in-out;
`;

const CopyWodHeader = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: ${props => props.isViewCompleteCopy ? `6px` : `0px`};

  width: 87.38%;
  height: ${props => props.isViewCompleteCopy ? `25px` : `0px`};

  opacity: ${props => props.opacity};

  border-radius: 5px;

  overflow: hidden;

  z-index: 1;

  background-color: ${props => props.theme.subKey};
  box-shadow: 0px 10px 30px 0px #3D975E;

  transition: opacity 0.3s ease-in-out, height 0.3s ease-in-out, margin-top 0.3s ease-in-out;
`;

const PasteWodTotalWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
  height: ${props => props.isView ? '100%' : '0px'};

  border-radius: 5px;

  overflow: hidden;

  z-index: 3;
`;

const PasteWodHeader = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  margin-top: ${props => props.isViewCompleteCopy ? `6px` : `0px`};

  width: 100%;
  height: 100%;

  opacity: ${props => props.opacity};

  border-radius: 5px;

  overflow: hidden;

  z-index: 3;

  background-color: ${props => props.bgColor};

  transition: opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out;
`;

const PasteButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  z-index: 5;
`;

const PasteButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: 8px;

  width: 87.38%;
  height: 25px;

  border-radius: 5px;
  
  background-color: ${props => props.theme.primary};
  box-shadow: 0px 10px 30px 0px ${props => props.theme.primary};
`;

const PasteExitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-top: 12px;
  
  width: 87.38%;
  height: 25px;

  border: 1px solid ${props => props.theme.primary};
  border-radius: 5px;

  background-color: ${props => props.bgColor};
  box-shadow: 0px 10px 30px 0px ${props => props.theme.primary};
`;

const ExitButton = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  transform:rotate(45deg);
`;

const ExitWidth = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: ${props => props.width}px;
  height: ${props => props.height}px;

  border-radius: ${props => props.radius}px;
  background-color: ${props => props.theme.primary};
`;

const WodStoreWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 0px 2px;

  width: calc(100% - 4px);

  overflow: hidden;
`;

const StoreInfoWrapper = styled.div`
  display: flex;
  flex-direction: ${props => props.isMid ? `column` : `row`};

  justify-content: center;
  align-items: flex-start;

  gap: 3px;
`;

const StoreLogoWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: ${props => props.size}px;
  height: ${props => props.size}px;

  overflow: hidden;

  opacity: ${props => props.isLock ? '0.5' : '1'};
  border-radius: 4px;

  transition: opacity 0.3s ease-in-out;
`;

const StoreTextInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: ${props => props.width};
  height: ${props => props.height};

  overflow: hidden;
`;

const LockerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 15px;
  min-width: 15px;
  height: 21px;
  min-height: 21px;

  &:hover {
    cursor: pointer;
  }
`;

const LockerImage = styled.img`
  width: 100%;
  height: 100%;
`;

const WriteWodBox = styled.div`
  display: flex;
  flex-direction: column;
  
  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 10px;

  width: calc(100% - 4px);
  min-height: 200px;

  gap: 20px;

  border: ${props => props.isClicked ? `2px solid ${props.theme.primary}` : `2px solid ${props.bgColor}`};
  border-radius: 4px;
  background-color: ${props => props.isClicked ? props.theme.primaryAlpha10 : props.bgColor};

  transition: border 0.3s ease-in-out, background-color 0.3s ease-in-out;
`;

const WodPrevViewWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const WodPrevViewSectionTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 4px;

  width: 100%;
`;

const SectionBox = styled.div`
  display: flex;
  flex-direction: column;
  
  justify-content: center;
  align-items: center;

  margin-top: 2px;

  width: 10px;
  min-width: 10px;
  height: 10px;
  min-height: 10px;

  border-radius: 4px;
  background-color: ${props => props.theme.subKey};
`;

const EmptyWodBox = styled.div`
  display: flex;
  flex-direction: column;
  
  justify-content: center;
  align-items: center;

  width: 100%;
  height: 22px;

  border-radius: 5px;
  background-color: rgba(255, 139, 72, 0.05);

  &:hover {
    cursor: pointer;
  }
`;

const WodAddButton = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  
  justify-content: center;
  align-items: center;

  width: calc(100% - 2px);
  height: ${props => props.isMinimum ? `18px` : `24px`};

  border: ${props => props.border};
  border-radius: ${props => props.isMinimum ? `5px` : `30px`};
  background-color: ${props => props.backgroundColor};

  &:hover {
    cursor: pointer;
  }

  transition: border 0.3s ease-in-out, background-color 0.3s ease-in-out;
`;

const PenImage = styled.img`
  position: absolute;

  width: ${props => props.isMinimum ? `10px` : `14px`};
  height: ${props => props.isMinimum ? `10px` : `14px`};

  opacity: ${props => props.isClicked ? `1` : `0`};

  transition: opacity 0.3s ease-in-out;
`;

const PlusImage = styled.img`
  position: absolute;

  width: ${props => props.isMinimum ? `12px` : `20px`};
  height: ${props => props.isMinimum ? `12px` : `20px`};

  opacity: ${props => props.isClicked ? `0` : `1`};

  transition: opacity 0.3s ease-in-out;
`;

const PreText = styled.pre`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  
  width: 100%;

  white-space: pre-wrap;
  word-wrap: break-word;

  &:hover {
    cursor: ${props => props.hover};
  }

  transition: color 0.3s ease-in-out;
`;


const VideoViewWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: ${props => props.width}px;
  height: ${props => props.height}px;
  
  border-radius: 5px;
  overflow: hidden;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  
  &:hover {
    cursor: ${props => props.hover};
  }

  transition: color 0.3s ease-in-out;
`;

const SmallText = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  zoom: 0.8;
  &:hover {
    cursor: ${props => props.hover};
  }

  transition: color 0.3s ease-in-out;
`;

const SavingBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  margin-bottom: 2px;

  width: 96%;
  height: 24px;

  overflow: hidden;

  border-radius: 5px;
  background-color: ${props => props.theme.primary};
`;
